import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import type { BlogPost } from '../../data/blog';
import { formatBlogDate } from '../../data/blog';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { BlogRichText } from './BlogRichText';

interface BlogPostViewProps {
  post: BlogPost;
}

function sectionNumber(index: number) {
  return String(index + 1).padStart(2, '0');
}

function authorInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export function BlogPostView({ post }: BlogPostViewProps) {
  const { ref: bodyRef, visible: bodyVisible } = useScrollReveal<HTMLElement>(0.1);

  return (
    <article className="blog-post blog-post--rich">
      <section className="blog-post__hero">
        <div className="container">
          <Link to="/blog" className="blog-post__back">
            ← Back to blog
          </Link>

          <div className="blog-post__hero-grid">
            <header className="blog-post__hero-copy">
              <span className="blog-post__tag">Blog / {post.category}</span>
              <h1 className="blog-post__title">{post.title}</h1>

              <div className="blog-post__chips">
                <span className="blog-post__chip">
                  <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
                </span>
                <span className="blog-post__chip">{post.readTime}</span>
              </div>

              <div className="blog-post__author">
                <span className="blog-post__author-avatar" aria-hidden>
                  {authorInitials(post.author.name)}
                </span>
                <div className="blog-post__author-meta">
                  <span className="blog-post__author-name">{post.author.name}</span>
                  <span className="blog-post__author-role">{post.author.role}</span>
                </div>
              </div>
            </header>

            <figure className="blog-post__cover">
              <span className="blog-post__cover-ring blog-post__cover-ring--outer" aria-hidden />
              <span className="blog-post__cover-ring blog-post__cover-ring--inner" aria-hidden />
              <div className="blog-post__cover-frame">
                <img src={post.image} alt="" className="blog-post__cover-image" loading="eager" />
              </div>
            </figure>
          </div>

          <blockquote className="blog-post__deck">
            <span className="blog-post__deck-mark" aria-hidden>
              "
            </span>
            <p>{post.excerpt}</p>
          </blockquote>
        </div>

        <div className="blog-post__orbit" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="blog-post__orbit-dot"
              style={{ '--orbit-i': i } as CSSProperties}
            />
          ))}
        </div>
      </section>

      <section ref={bodyRef} className={`blog-post__body${bodyVisible ? ' is-visible' : ''}`}>
        <div className="container">
          <div className="blog-post__stream">
            {post.sections.map((section, index) => (
              <article
                key={section.heading ?? `section-${index}`}
                className={`blog-post__entry reveal-item${index % 2 === 1 ? ' blog-post__entry--alt' : ''}`}
                style={{ transitionDelay: `${index * 0.06}s` }}
              >
                <div className="blog-post__entry-grid">
                  <span className="blog-post__entry-num" aria-hidden>
                    {sectionNumber(index)}
                  </span>

                  <div className="blog-post__entry-copy">
                    {section.heading ? (
                      <header className="blog-post__entry-head">
                        <h2>{section.heading}</h2>
                      </header>
                    ) : null}

                    <div className="blog-post__entry-body">
                      {section.paragraphs.map((paragraph, paragraphIndex) => (
                        <BlogRichText
                          key={paragraph.parts.map((part) => ('value' in part ? part.value : part.href)).join('-')}
                          parts={paragraph.parts}
                          className={
                            index === 0 && paragraphIndex === 0 ? 'blog-post__lead' : undefined
                          }
                        />
                      ))}

                      {section.bullets && section.bullets.length > 0 ? (
                        <ul className="blog-post__list">
                          {section.bullets.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      ) : null}

                      {section.links && section.links.length > 0 ? (
                        <div className="blog-post__links">
                          <span className="blog-post__links-label">Continue exploring</span>
                          <div className="blog-post__links-row">
                            {section.links.map((link) =>
                              link.href.startsWith('http') ? (
                                <a
                                  key={link.href}
                                  href={link.href}
                                  className="blog-post__link-chip"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {link.label}
                                </a>
                              ) : (
                                <Link key={link.href} to={link.href} className="blog-post__link-chip">
                                  {link.label}
                                </Link>
                              )
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <footer className="blog-post__end reveal-item">
            <p className="blog-post__end-note">
              Enjoyed this article?{' '}
              <Link to="/waitlist" className="blog-post__inline-link">
                Join the waitlist
              </Link>{' '}
              or browse more on the{' '}
              <Link to="/blog" className="blog-post__inline-link">
                blog
              </Link>
              .
            </p>
            <Link to="/blog" className="blog-post__end-link">
              ← All articles
              <svg viewBox="0 0 16 16" aria-hidden className="blog-post__end-arrow">
                <path
                  fill="currentColor"
                  d="M3 8h8.59L8.29 4.71 9.71 3.29 14.41 8l-4.7 4.71-1.42-1.42L11.59 9H3V8z"
                />
              </svg>
            </Link>
          </footer>
        </div>
      </section>
    </article>
  );
}
